<template>
  <div class="priority-filter-container">
    <div class="filter-group">
      <label>{{ $t('PRIORITY_CONTACTS_PAGE.LIST.TABLE_HEADER.INBOX') }}</label>
      <select v-model="filters.inboxId" :disabled="disabled">
        <option :value="null">
          {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.ALL') }}
        </option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>{{
        $t('PRIORITY_CONTACTS_PAGE.LIST.TABLE_HEADER.ASSIGNED_TEAM')
        }}</label>
      <select v-model="filters.teamId" :disabled="disabled">
        <option :value="null">
          {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.ALL') }}
        </option>
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>{{
        $t('PRIORITY_CONTACTS_PAGE.LIST.TABLE_HEADER.ASSIGNED_AGENT')
        }}</label>
      <select v-model="filters.agentId" :disabled="disabled">
        <option :value="null">
          {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.ALL') }}
        </option>
        <option v-for="user in selectableUsers" :key="user.id" :value="user.id">
          {{ user.name }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>{{
        $t('PRIORITY_CONTACTS_PAGE.LIST.TABLE_HEADER.PRIORITY')
        }}</label>
      <select v-model="filters.priority" :disabled="disabled">
        <option :value="null">
          {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.ALL') }}
        </option>
        <option value="low">
          {{ $t('PRIORITY_CONTACTS_PAGE.PRIORITY.LOW') }}
        </option>
        <option value="medium">
          {{ $t('PRIORITY_CONTACTS_PAGE.PRIORITY.MEDIUM') }}
        </option>
        <option value="high">
          {{ $t('PRIORITY_CONTACTS_PAGE.PRIORITY.HIGH') }}
        </option>
        <option value="urgent">
          {{ $t('PRIORITY_CONTACTS_PAGE.PRIORITY.URGENT') }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label>{{ $t('PRIORITY_CONTACTS_PAGE.LIST.TABLE_HEADER.LAST_MESSAGE_BY') }}</label>
      <select v-model="filters.lastMessageBy" :disabled="disabled">
        <option :value="null">
          {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.ALL') }}
        </option>
        <option value="contact">
          {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.LAST_MESSAGE_BY.CONTACT') }}
        </option>
        <option value="agent">
          {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.LAST_MESSAGE_BY.AGENT') }}
        </option>
        <option value="automation">
          {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.LAST_MESSAGE_BY.AUTOMATION') }}
        </option>
      </select>
    </div>

    <div class="button-wrapper">
      <button class="apply-button" @click="applyFilters" :disabled="disabled">
        {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.APPLY') }}
      </button>
      <button class="apply-button clear-button" @click="clearFilters" :disabled="disabled">
        {{ $t('PRIORITY_CONTACTS_PAGE.FILTERS.CLEAR') }}
      </button>
    </div>
  </div>
</template>

<script>
const UNASSIGNED_FILTER_VALUE = 'unassigned';

export default {
  name: 'PriorityConversationsFilter',
  props: {
    paginatedList: {
      type: Array,
      required: true,
    },
    availableFiltersData: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    initialFilters: {
      type: Object,
      required: true,
    },
    isAgentFilterLocked: {
      type: Boolean,
      default: false,
    },
    restrictedAgentId: {
      type: [Number, String],
      default: null,
    },
    restrictedAgentName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      filters: {
        inboxId: null,
        teamId: null,
        agentId: null,
        priority: null,
        lastMessageBy: null,
      },
    };
  },
  computed: {
    inboxes() {
      return (this.availableFiltersData.inboxes || []).map(i => ({
        id: i.id,
        name: this.truncateName(i.name),
      }));
    },
    teams() {
      return (this.availableFiltersData.teams || []).map(t => ({
        id: t.id,
        name: this.truncateName(t.name),
      }));
    },
    users() {
      return (this.availableFiltersData.users || []).map(u => ({
        id: u.id,
        name: this.truncateName(u.name),
      }));
    },
    restrictedAgentOption() {
      if (!this.restrictedAgentId) {
        return null;
      }

      const restrictedUser = this.users.find(
        user => String(user.id) === String(this.restrictedAgentId)
      );

      if (restrictedUser) {
        return restrictedUser;
      }

      return {
        id: this.restrictedAgentId,
        name: this.truncateName(this.restrictedAgentName),
      };
    },
    selectableUsers() {
      if (!this.isAgentFilterLocked) {
        return this.users;
      }

      return this.restrictedAgentOption ? [this.restrictedAgentOption] : [];
    },
  },
  watch: {
    initialFilters: {
      handler(newVal) {
        this.filters = { ...newVal };
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    truncateName(name, limit = 20) {
      if (!name) return '---';
      return name.length > limit ? name.slice(0, limit - 3) + '...' : name;
    },
    applyFilters() {
      this.$emit('filter-applied', this.filters);
    },
    clearFilters() {
      this.filters = {
        inboxId: null,
        teamId: null,
        agentId: this.isAgentFilterLocked ? this.restrictedAgentId : null,
        priority: null,
        lastMessageBy: null,
      };
      this.$emit('filter-applied', this.filters);
    },
  },
};
</script>

<style scoped>
.priority-filter-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
  padding-left: 1.7rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 160px;
}

.filter-group select {
  height: 36px;
  padding: 0.25rem 0.5rem;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.apply-button {
  height: 36px;
  padding: 0 1rem;
  background-color: #0041c2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 5px;
  margin-right: 0.5rem;
}

.clear-button {
  background-color: #888;
}

.apply-button:disabled,
.clear-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-wrapper {
  display: flex;
  align-self: flex-start;
  padding-top: 20px;
}
</style>
